import { OrderResponse } from '@/types/order-type';
import { useEffect, useRef, useState } from 'react';

const PAID_ALERT_MS = 3000;
const PAYMENT_POLL_MS = 5000;

export function isOrderPaid(
  order: Pick<OrderResponse, 'status' | 'paymentStatus'> | null | undefined,
): boolean {
  if (!order) return false;
  return (
    order.status === 'PAID' ||
    order.paymentStatus === 'PAID' ||
    order.paymentStatus === 'CAPTURED'
  );
}

interface UsePublicOrderPaymentOptions {
  order: OrderResponse | null;
  enableABA: boolean;
  refresh: (silent?: boolean) => void;
}

export function usePublicOrderPayment({
  order,
  enableABA,
  refresh,
}: UsePublicOrderPaymentOptions) {
  const isPaid = isOrderPaid(order);
  const [showPaidAlert, setShowPaidAlert] = useState(false);
  const hasShownPaidAlert = useRef(false);

  useEffect(() => {
    if (!isPaid || hasShownPaidAlert.current) return;

    hasShownPaidAlert.current = true;
    setShowPaidAlert(true);
    const timer = window.setTimeout(() => setShowPaidAlert(false), PAID_ALERT_MS);
    return () => window.clearTimeout(timer);
  }, [isPaid]);

  useEffect(() => {
    if (!order || isPaid || enableABA) return;

    const interval = window.setInterval(() => refresh(true), PAYMENT_POLL_MS);
    return () => window.clearInterval(interval);
  }, [order, isPaid, enableABA, refresh]);

  return { isPaid, showPaidAlert };
}
